import { Button } from '@components/button';
import { Tab, TabPanel, Tabs } from '@components/tabs';
import {
  EditWorkspaceMutationVariables,
  useEditWorkspaceMutation,
  useGetAnnotationMapsQuery,
  useGetGANModelsQuery,
  useGetWorkspaceResourcesAvailableChannelsQuery,
  useGetWorkspaceResourcesAvailableVolumesQuery,
  useGetWorkspaceResourcesWorkspaceChannelsQuery,
  useGetWorkspaceResourcesWorkspaceVolumesQuery,
} from '@generated/graphql';
import { Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { ChangeEvent, useState } from 'react';
import { AnnotationTransferList } from './AnnotationTransferList';
import { ChannelsTransferList } from './ChannelsTransferList';
import { GanTransferList } from './GanTransferList';
import { ResourcesType } from './TransferList';
import { VolumesTransferList } from './VolumesTransferList';

type WorkspaceResourcesModalProps = {
  onClose: () => void;
  organizationId: string;
  tab?: string;
  workspaceId: string;
};

export const WorkspaceResourcesModal = ({
  organizationId,
  tab = 'channels',
  workspaceId,
  onClose,
}: WorkspaceResourcesModalProps) => {
  const [selectedChannels, setSelectedChannels] = useState<ResourcesType>();
  const [selectedVolumes, setSelectedVolumes] = useState<ResourcesType>();
  const [selectedGanModels, setSelectedGanModels] = useState<ResourcesType>();
  const [selectedAnnotations, setSelectedAnnotations] = useState<ResourcesType>();
  const [channelsDirty, setChannelsDirty] = useState(false);
  const [volumesDirty, setVolumesDirty] = useState(false);
  const [ganDirty, setGanDirty] = useState(false);
  const [annotationsDirty, setAnnotationsDirty] = useState(false);

  const [tabValue, setTabValue] = useState<string>(tab);
  const handleTabsChange = (_: ChangeEvent<HTMLInputElement>, value: string) => {
    setTabValue(value);
  };

  const [editWorkspaceMutation] = useEditWorkspaceMutation();
  const { createAsyncNotification } = useNotifications();

  const { refetch: availableChannelsRefetch } = useGetWorkspaceResourcesAvailableChannelsQuery({
    variables: {
      organizationId,
    },
  });
  const { refetch: selectedChannelsRefetch } = useGetWorkspaceResourcesWorkspaceChannelsQuery({
    variables: {
      workspaceId,
    },
  });
  const { refetch: availableVolumesRefetch } = useGetWorkspaceResourcesAvailableVolumesQuery({
    variables: {
      organizationId,
    },
  });
  const { refetch: selectedVolumesRefetch } = useGetWorkspaceResourcesWorkspaceVolumesQuery({
    variables: {
      workspaceId,
    },
  });
  const { refetch: availableGanModelsRefetch } = useGetGANModelsQuery({
    variables: { organizationId },
  });
  const { refetch: selectedGanModelsRefetch } = useGetGANModelsQuery({
    variables: {
      workspaceId,
    },
  });
  const { refetch: availableAnnotationsRefetch } = useGetAnnotationMapsQuery({
    variables: { organizationId },
  });
  const { refetch: selectedAnnotationsRefetch } = useGetAnnotationMapsQuery({
    variables: {
      workspaceId,
    },
  });

  const handleSave = createAsyncNotification('Successfully saved resources', async () => {
    onClose();
    if (!channelsDirty && !volumesDirty && !ganDirty && !annotationsDirty) {
      return;
    }
    const variables = { workspaceId } as EditWorkspaceMutationVariables;
    if (channelsDirty) {
      variables.channelIds = (selectedChannels ?? []).map((c) => c.id);
    }
    if (volumesDirty) {
      variables.volumeIds = (selectedVolumes ?? []).map((v) => v.id);
    }
    if (ganDirty) {
      variables.ganIds = (selectedGanModels ?? []).map((g) => g.id);
    }
    if (annotationsDirty) {
      variables.mapIds = (selectedAnnotations ?? []).map((a) => a.id);
    }
    await editWorkspaceMutation({
      variables,
    });
    await availableChannelsRefetch();
    await selectedChannelsRefetch();
    await availableVolumesRefetch();
    await selectedVolumesRefetch();
    await availableGanModelsRefetch();
    await selectedGanModelsRefetch();
    await availableAnnotationsRefetch();
    await selectedAnnotationsRefetch();
  });
  const channelsTransferListProps = {
    organizationId,
    workspaceId,
    selectedChannels,
    setSelectedChannels,
    setChannelsDirty,
  };
  const volumesTransferListProps = {
    organizationId,
    workspaceId,
    selectedVolumes,
    setSelectedVolumes,
    setVolumesDirty,
  };
  const ganModelsTransferListProps = {
    organizationId,
    workspaceId,
    selectedGanModels,
    setSelectedGanModels,
    setGanDirty,
  };
  const annotationsTransferListProps = {
    organizationId,
    workspaceId,
    selectedAnnotations,
    setSelectedAnnotations,
    setAnnotationsDirty,
  };

  return (
    <Stack gap={9} data-cy="Workspaces-Resources-Modal">
      <Tabs value={tabValue} onChange={handleTabsChange}>
        <Tab value="channels" label="Channels" data-cy="Workspaces-Resources-Modal-Channels-Tab" />
        <Tab value="volumes" label="Volumes" data-cy="Workspaces-Resources-Modal-Volumes-Tab" />
        <Tab value="gan" label="GAN models" data-cy="Workspaces-Resources-Modal-GAN-Tab" />
        <Tab
          value="annotation"
          label="Annotation maps"
          data-cy="Workspaces-Resources-Modal-Annotations-Tab"
        />
      </Tabs>

      <TabPanel value={tabValue} index="channels">
        <ChannelsTransferList {...channelsTransferListProps} />
      </TabPanel>
      <TabPanel value={tabValue} index="volumes">
        <VolumesTransferList {...volumesTransferListProps} />
      </TabPanel>
      <TabPanel value={tabValue} index="gan">
        <GanTransferList {...ganModelsTransferListProps} />
      </TabPanel>
      <TabPanel value={tabValue} index="annotation">
        <AnnotationTransferList {...annotationsTransferListProps} />
      </TabPanel>

      <Stack direction="row" justifyContent="flex-end" gap={4}>
        <Button variant="primary" onClick={handleSave} sx={{ width: '100%', maxWidth: '128px' }}>
          Save
        </Button>
        <Button variant="secondary" onClick={onClose} sx={{ width: '100%', maxWidth: '128px' }}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
